import { useState, useEffect } from 'react'
import { Autocomplete, Box, TextField } from '@mui/material'
import { allCountries, allCountryCodes } from 'material-ui-phone-number/src/country_data'
import 'flag-icons/css/flag-icons.min.css'

const COUNTRY_OPTIONS = allCountries.filter((c) => !c.isAreaCode)

const getCountryByIso2 = (iso2) =>
  COUNTRY_OPTIONS.find((c) => c.iso2 === iso2) || null

const parsePhone = (value) => {
  if (!value || !value.startsWith('+')) return null
  const digits = value.slice(1)
  for (const len of [4, 3, 2, 1]) {
    const dc = digits.slice(0, len)
    if (allCountryCodes[dc]?.[0]) {
      return { iso2: allCountryCodes[dc][0], dialCode: dc, localNumber: digits.slice(len) }
    }
  }
  return null
}

const PhoneSplit = ({
  value = '',
  onChange,
  defaultCountry = 'fr',
  label,
  error,
  helperText,
  disabled,
  className,
  color,
  variant = 'outlined',
  fullWidth,
  size,
  style,
}) => {
  const [country, setCountry] = useState(() => {
    if (value) {
      const parsed = parsePhone(value)
      if (parsed) return getCountryByIso2(parsed.iso2)
    }
    return getCountryByIso2(defaultCountry) || getCountryByIso2('fr')
  })

  const [localNumber, setLocalNumber] = useState(() => {
    if (value) {
      if (value.startsWith('+')) {
        const parsed = parsePhone(value)
        if (parsed) return parsed.localNumber
      } else {
        return value
      }
    }
    return ''
  })

  useEffect(() => {
    const currentCombined = localNumber && country
      ? `+${country.dialCode}${localNumber}`
      : ''
    if (value === currentCombined) return

    if (!value) {
      setLocalNumber('')
      return
    }
    if (!value.startsWith('+')) {
      setLocalNumber(value)
      return
    }
    const parsed = parsePhone(value)
    if (!parsed) return
    const newCountry = getCountryByIso2(parsed.iso2)
    if (newCountry) setCountry(newCountry)
    setLocalNumber(parsed.localNumber)
  }, [value])

  const handleCountryChange = (_, newCountry) => {
    if (!newCountry) return
    setCountry(newCountry)
    const combined = localNumber ? `+${newCountry.dialCode}${localNumber}` : ''
    onChange?.(combined)
  }

  const handleNumberChange = (e) => {
    const num = e.target.value.replace(/[^0-9]/g, '')
    setLocalNumber(num)
    const combined = num ? `+${country?.dialCode || ''}${num}` : ''
    onChange?.(combined)
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': { background: 'white' },
    '& .MuiOutlinedInput-root fieldset': { borderRadius: '2px' },
    '&& .MuiOutlinedInput-root.Mui-focused fieldset': { borderWidth: '1px' },
  }

  return (
    <Box
      className={className}
      style={{ height: 'fit-content', ...style }}
      sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: fullWidth ? '100%' : undefined }}
    >
      <Autocomplete
        options={COUNTRY_OPTIONS}
        value={country}
        onChange={handleCountryChange}
        disableClearable
        disabled={disabled}
        size={size}
        getOptionLabel={(opt) => `+${opt.dialCode}`}
        renderOption={(props, opt) => (
          <li {...props} key={`${opt.iso2}-${opt.dialCode}`}>
            <span className={`fi fi-${opt.iso2.toLowerCase()}`} style={{ marginRight: 8, borderRadius: 2, flexShrink: 0 }} />
            +{opt.dialCode} {opt.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={variant}
            color={color}
            size={size}
            sx={fieldSx}
            InputProps={{
              ...params.InputProps,
              startAdornment: country ? (
                <span className={`fi fi-${country.iso2.toLowerCase()}`} style={{ marginRight: 6, borderRadius: 2, flexShrink: 0 }} />
              ) : null,
            }}
          />
        )}
        isOptionEqualToValue={(opt, val) => opt.iso2 === val?.iso2 && opt.dialCode === val?.dialCode}
        sx={{ minWidth: 120 }}
      />
      <TextField
        variant={variant}
        color={color}
        size={size}
        label={label}
        value={localNumber}
        onChange={handleNumberChange}
        error={error}
        helperText={helperText}
        disabled={disabled}
        inputProps={{ maxLength: 15 }}
        sx={{ flex: 1, ...fieldSx }}
      />
    </Box>
  )
}

export default PhoneSplit
